import {Injectable} from '@angular/core';
import {HttpClient, HttpErrorResponse} from '@angular/common/http';
import 'rxjs/add/operator/map';
import 'rxjs/add/operator/catch';
import 'rxjs/add/observable/throw';
import {List, Enumerable} from 'linq-collections';
import {IList} from 'linq-collections/build/src/Collections';
import {Observable} from 'rxjs/Observable';

@Injectable()
export class DataProviderService {
  private static readonly _urls = {
    teamMembers: 'assets/json/team.json',
    serviceCards: 'assets/json/services.json',
    referenceProjects: 'assets/json/referenceProjects.json',
    ourStory: 'assets/json/ourStory.json',
    partners: 'assets/json/partners.json',
    profileImageMale: 'assets/img/team/man.svg',
    profileImageFemale: 'assets/img/team/woman.svg',
  };
  url: string = 'assets/json/ourStory.json';

  constructor(private _httpClient: HttpClient) {
  }

  //#region getTeamMembers
  public getTeamMembers(): Observable<IList<ITeamMember>> {
    return this._httpClient.get<ITeamSource>(DataProviderService._urls.teamMembers)
      .map(this._mapTeamMemberList)
      .catch(this.handleHttpError)
      .map(res => res instanceof List ? res : new List<ITeamMember>());
  }

  private _mapTeamMemberList = (source: ITeamSource): IList<ITeamMember> => {
    let teamMembersList = Enumerable.fromSource(source.members)
      .select(DataProviderService._convertToTeamMember)
      .toList();

    return teamMembersList;
  }

  private static _convertToTeamMember(teamMemberRaw: ITeamMemberRaw): ITeamMember {
    let name = `${teamMemberRaw.lastName} ${teamMemberRaw.firstName}`; // TODO localizaiton
    let image = teamMemberRaw.image;
    image = image || (teamMemberRaw.isMale ? DataProviderService._urls.profileImageMale : DataProviderService._urls.profileImageFemale);

    let teamMember = <ITeamMember>{
      name: name,
      jobTitle: teamMemberRaw.jobTitle,
      group: teamMemberRaw.group,
      isMale: teamMemberRaw.isMale,
      image: image,
      twitterUrl: teamMemberRaw.twitterUrl || null,
      facebookUrl: teamMemberRaw.facebookUrl || null,
      linkedInUrl: teamMemberRaw.linkedInUrl || null,
    };
    return teamMember;
  }

  //#endregion getTeamMembers

  //#region getServiceCards
  public getServiceCards(url: string): Observable<IList<IServiceCard>> {
    return this._httpClient.get<IServicesSource>(url)
      .map(this._mapServiceCardList)
      .catch(this.handleHttpError)
      .map(res => res instanceof List ? res : new List<IServiceCard>());
  }

  private _mapServiceCardList = (source: IServicesSource): IList<IServiceCard> => {
    let serviceCardsList = Enumerable.fromSource(source.services)
      .select(DataProviderService._convertToServiceCard)
      .toList();

    return serviceCardsList;
  }

  private static _convertToServiceCard(serviceCard: IServiceCard): IServiceCard {
    return serviceCard;
  }

  //#endregion getServiceCards

  //#region getReferenceProjects
  public getReferenceProjects(url: string): Observable<IList<IReferenceProject>> {
    return this._httpClient.get<IReferenceProjectsSource>(url)
      .map(this._mapReferenceProjectList)
      .catch(this.handleHttpError)
      .map(res => res instanceof List ? res : new List<IReferenceProject>());
  }

  private _mapReferenceProjectList = (source: IReferenceProjectsSource): IList<IReferenceProject> => {
    let referenceProjectsList = Enumerable.fromSource(source.references)
      .select(DataProviderService._convertToReferenceProject)
      .toList();

    return referenceProjectsList;
  }

  private static _convertToReferenceProject(referenceProject: IReferenceProjectRaw): IReferenceProject {
    let style = ReferenceProjectStyle[referenceProject.style] || ReferenceProjectStyle.unknown;
    if (!style) {
      console.warn(`"Unknown reference project style found: ${referenceProject.style}`);
    }

    let result = <IReferenceProject>{
      title: referenceProject.title,
      imageUrl: referenceProject.imageUrl,
      description: referenceProject.description,
      subTitle: referenceProject.subTitle,
      style: style,
      link: referenceProject.link || null,
    };
    return result;
  }

  //#endregion getReferenceProjects

  //#region getStoryBoardItems
  public getStoryBoardItems(url: string): Observable<IList<IStoryBoardItem>> {
    return this._httpClient.get<IStoryBoardItemsSource>(url)
      .map(this._mapStoryBoardItemList)
      .catch(this.handleHttpError)
      .map(res => res instanceof List ? res : new List<IStoryBoardItem>());
  }

  private _mapStoryBoardItemList = (source: IStoryBoardItemsSource): IList<IStoryBoardItem> => {
    let storyBoardItemsList = Enumerable.fromSource(source.storyBoard)
      .select(DataProviderService._convertToStoryBoardItem)
      .toList();

    return storyBoardItemsList;
  }

  private static _convertToStoryBoardItem(storyBoardItem: IStoryBoardItem): IStoryBoardItem {
    return storyBoardItem;
  }

  //#endregion getStoryBoardItems

  //#region getPartners
  public getPartners(url: string): Observable<IList<IPartner>> {
    return this._httpClient.get<IPartnersSource>(url)
      .map(this._mapPartnerList)
      .catch(this.handleHttpError)
      .map(res => res instanceof List ? res : new List<IPartner>());
  }

  private _mapPartnerList = (source: IPartnersSource): IList<IPartner> => {
    let partnersList = Enumerable.fromSource(source.partners)
      .select(DataProviderService._convertToPartner)
      .toList();

    return partnersList;
  }

  private static _convertToPartner(partner: IPartner): IPartner {
    partner.webPage = partner.webPage || null;

    return partner;
  }

  //#endregion getPartners

  protected handleHttpError(error: HttpErrorResponse): any {
    console.log(error);
    return Observable.throw(error);
  }
}

//#region TeamMember
interface ITeamSource {
  members: ITeamMemberRaw[];
}

interface ITeamMemberRaw {
  firstName: string;
  lastName: string;
  jobTitle: string;
  group: string;
  isMale: boolean;
  image: string;
  twitterUrl: string;
  facebookUrl: string;
  linkedInUrl: string;
}

export interface ITeamMember {
  name: string;
  jobTitle: string;
  group: string;
  isMale: boolean;
  image: string;
  twitterUrl: string;
  facebookUrl: string;
  linkedInUrl: string;
}

//#endregion

//#region ServiceCard
interface IServicesSource {
  services: IServiceCard[];
}

export interface IServiceCard {
  name: string;
  headerImage: string;
  headerText: string;
  description: string;
}

//#endregion

//#region ReferenceProject
interface IReferenceProjectsSource {
  references: IReferenceProjectRaw[];
}

interface IReferenceProjectRaw {
  title: string;
  imageUrl: string;
  link: string;
  description: string;
  subTitle: string;
  style: string;
}

export interface IReferenceProject {
  title: string;
  imageUrl: string;
  link: string;
  description: string;
  subTitle: string;
  style: ReferenceProjectStyle;
}

export enum ReferenceProjectStyle {
  unknown = 0,
  webPage,
  desktopApp,
  mobileApp,
  companyLogo,
}

//#endregion

//#region StoryBoardItem
interface IStoryBoardItemsSource {
  storyBoard: IStoryBoardItem[];
}

export interface IStoryBoardItem {
  image: string;
  imagePlaceholderChar: string;
  title: string;
  subTitle: string;
  description: string;
}

//#endregion

//#region Partner
interface IPartnersSource {
  partners: IPartner[];
}

export interface IPartner {
  name: string;
  image: string;
  webPage: string;
}

// #endregion



// WEBPACK FOOTER //
// C:/Projects/dod/companyintroduction/src/app/shared/data-provider.service.ts